import React, { useState, useEffect, useCallback } from 'react';
import { Search, Filter, Calendar, User, Clock } from 'lucide-react';
import { Table } from '../components/ui/Table';
import { driverApi, DriverEvent, DriverUser } from '../services/driverApi';
import { UserDetailsModal } from '../components/driver/UserDetailsModal';

export const DriverAudit: React.FC = () => {
  const [events, setEvents] = useState<DriverEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    event_type: 'all',
    driver_user: '',
    start_date: '',
    end_date: '',
    status: 'all'
  });
  const [selectedUser, setSelectedUser] = useState<DriverUser | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        page_size: pagination.page_size
      };

      if (filters.search) params.search = filters.search;
      if (filters.event_type !== 'all') params.event_type = filters.event_type;
      if (filters.driver_user) params.driver_user = filters.driver_user;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.status !== 'all') params.status = filters.status;

      const response = await driverApi.getEvents(params);
      setEvents(response.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0,
        page_length: response.pagination?.page_size || prev.page_size
      }));
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.page_size, filters]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      event_type: 'all',
      driver_user: '',
      start_date: '',
      end_date: '',
      status: 'all'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewUser = async (driverUserName: string) => {
    try {
      const response = await driverApi.getUser(driverUserName);
      setSelectedUser(response.user);
      setShowUserDetails(true);
    } catch (error) {
      console.error('Failed to load user details:', error);
    }
  };

  const handleCloseUserDetails = () => {
    setShowUserDetails(false);
    setSelectedUser(null);
  };

  const handleAddEsim = () => {
    // This would open the Add eSIM modal
    console.log('Add eSIM functionality');
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'user_created':
        return '👤';
      case 'user_archived':
        return '📦';
      case 'esim_issued':
        return '📱';
      case 'email_sent':
        return '📧';
      case 'automatic_topup':
        return '🔄';
      case 'profile_status_changed':
        return '🔄';
      default:
        return '📝';
    }
  };

  const getEventTypeLabel = (eventType: string) => {
    return eventType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatEventData = (data: string) => {
    try {
      const parsed = JSON.parse(data);
      return parsed.description || parsed.message || 'Event occurred';
    } catch {
      return data || 'Event occurred';
    }
  };

  const columns = [
    {
      key: 'event',
      header: 'EVENT',
      render: (value: any, row: DriverEvent) => (
        <div className="flex items-center">
          <span className="text-2xl mr-3">{getEventIcon(row.event_type)}</span>
          <div>
            <div className="font-medium text-gray-900">{getEventTypeLabel(row.event_type)}</div>
            <div className="text-sm text-gray-500">{row.event_type}</div>
          </div>
        </div>
      )
    },
    {
      key: 'user',
      header: 'USER',
      render: (value: any, row: DriverEvent) => (
        <div className="text-sm">
          {row.driver_user ? (
            <div>
              <div className="font-medium text-gray-900">
                {row.event_type === 'email_sent' ? (
                  <button
                    onClick={() => handleViewUser(row.driver_user)}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {row.first_name} {row.last_name}
                  </button>
                ) : (
                  `${row.first_name} ${row.last_name}`
                )}
              </div>
              <div className="text-sm text-gray-500">{row.email || row.driver_user}</div>
            </div>
          ) : (
            <span className="text-gray-500">System</span>
          )}
        </div>
      )
    },
    {
      key: 'timestamp',
      header: 'DATE & TIME',
      render: (value: any, row: DriverEvent) => (
        <div className="text-sm text-gray-900">
          {new Date(row.ts).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })}
        </div>
      )
    },
    {
      key: 'status',
      header: 'STATUS',
      render: (value: any, row: DriverEvent) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          SUCCESS
        </span>
      )
    },
    {
      key: 'details',
      header: 'DETAILS',
      render: (value: any, row: DriverEvent) => {
        const eventData = formatEventData(row.data || '');
        return (
          <div className="text-sm">
            <div className="text-gray-900">{eventData}</div>
            {row.data && JSON.parse(row.data || '{}') && Object.keys(JSON.parse(row.data || '{}')).length > 1 && (
              <button className="text-blue-600 hover:text-blue-800 text-xs mt-1">
                + more fields
              </button>
            )}
          </div>
        );
      }
    },
    {
      key: 'actions',
      header: 'ACTIONS',
      render: (value: any, row: DriverEvent) => (
        <div className="text-sm">
          {row.driver_user && (
            <button 
              onClick={() => handleViewUser(row.driver_user)}
              className="text-blue-600 hover:text-blue-800"
            >
              View User &gt;
            </button>
          )}
        </div>
      )
    }
  ];

  const eventTypes = [
    'all',
    'user_created',
    'user_archived',
    'esim_issued',
    'email_sent',
    'automatic_topup',
    'profile_status_changed'
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Audit & History</h1>
        <p className="text-gray-600 mt-1">Track all driver management activities and events</p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        {/* Search Field - Full Width */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by user email, metadata"
              value={filters.search}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
        
        {/* Filter Dropdowns - Next Line */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event Type</label>
            <select
              value={filters.event_type}
              onChange={(e) => handleFilterChange('event_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Events</option>
              {eventTypes.filter(type => type !== 'all').map(type => (
                <option key={type} value={type}>{getEventTypeLabel(type)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Driver User</label>
            <input
              type="text"
              placeholder="Driver ID or name"
              value={filters.driver_user}
              onChange={(e) => handleFilterChange('driver_user', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="mt-4 flex justify-between">
          <button
            onClick={handleResetFilters}
            className="text-sm text-gray-600 hover:text-gray-800"
          >
            Reset Filters
          </button>
          <div className="text-sm text-gray-600">
            Showing {events.length} of {pagination.total} events
          </div>
        </div>
      </div>

      {/* Events Table */}
      <Table
        columns={columns}
        data={events}
        isLoading={loading}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
        onPageSizeChange={(pageSize) => setPagination(prev => ({ ...prev, page_size: pageSize, page: 1 }))}
        emptyMessage="No events found"
      />

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={handleCloseUserDetails}
          onAddEsim={handleAddEsim}
        />
      )}
    </div>
  );
};
